'use client'

import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'

interface Capability {
  icon: React.ElementType
  label: string
  query: string
}

// ── Default capabilities (shown on Dashboard and unknown pages) ──────────────
const DEFAULT_CAPABILITIES: Capability[] = [
  { icon: Icons.barChart, label: 'System overview', query: 'Give me a system overview — how many solicitations, contracts and vendors do we have?' },
  { icon: Icons.layers, label: 'Show the pipeline', query: 'Show me every solicitation in the pipeline' },
  { icon: Icons.users, label: 'Show vendor list', query: 'Show me all available vendors' },
  { icon: Icons.fileText, label: 'Open exceptions', query: 'What open exceptions are outstanding across the estate?' },
  { icon: Icons.sparkles, label: 'Pending approvals', query: 'What is currently waiting for my approval?' },
]

// ── Solicitation workbench capabilities ──────────────────────────────────────
const TENDER_WORKBENCH_CAPABILITIES: Capability[] = [
  { icon: Icons.search, label: 'Solicitation status', query: 'What is the current status of this solicitation and what is the next step?' },
  { icon: Icons.shield, label: 'Policy compliance', query: 'Run a compliance check on this solicitation against every active Auto Policy' },
  { icon: Icons.fileText, label: 'Acquisition Plan', query: 'Summarize the Acquisition Plan for this solicitation' },
  { icon: Icons.layers, label: 'Evaluation results', query: 'Show me the proposal evaluation results for this solicitation' },
  { icon: Icons.sparkles, label: 'Next gate action', query: 'What gate decision is required for this solicitation and what evidence is needed?' },
  { icon: Icons.clipboardList, label: 'Audit trail', query: 'Show me the audit trail and key decisions made for this solicitation' },
]

// ── Solicitation pipeline capabilities ───────────────────────────────────────
const TENDER_PIPELINE_CAPABILITIES: Capability[] = [
  { icon: Icons.layers, label: 'Show all solicitations', query: 'Show me every solicitation in the pipeline grouped by stage' },
  { icon: Icons.barChart, label: 'Solicitations at gate', query: 'Which solicitations are currently awaiting a gate approval?' },
  { icon: Icons.plus, label: 'Create a solicitation', query: 'I want to open a new federal solicitation' },
  { icon: Icons.search, label: 'Find a solicitation', query: 'Help me find a specific solicitation' },
  { icon: Icons.shield, label: 'Policy library', query: 'Which Auto Policies apply before a solicitation is released?' },
  { icon: Icons.sparkles, label: 'FAR authority', query: 'What are the key obligations under FAR Part 12 for commercial items?' },
]

// ── Procurement (AP) capabilities ────────────────────────────────────────────
const PROCUREMENT_CAPABILITIES: Capability[] = [
  { icon: Icons.clipboardList, label: 'Create a purchase request', query: 'I need to create a new purchase request' },
  { icon: Icons.layers, label: 'List purchase requests', query: 'Show me all purchase requests' },
  { icon: Icons.sparkles, label: 'Pending approvals', query: 'What is currently waiting for my approval?' },
  { icon: Icons.barChart, label: 'Check department budget', query: 'Check budget availability for Engineering department' },
  { icon: Icons.users, label: 'Recommend vendors', query: 'I need vendor recommendations for IT equipment' },
  { icon: Icons.search, label: 'Pipeline status', query: 'Show me the overall procurement pipeline status' },
]

// ── PR detail capabilities ────────────────────────────────────────────────────
const PR_DETAIL_CAPABILITIES: Capability[] = [
  { icon: Icons.search, label: 'PR status', query: 'What is the current status of this PR and who needs to approve it?' },
  { icon: Icons.users, label: 'Recommend vendors', query: 'Recommend vendors for the item in this PR' },
  { icon: Icons.layers, label: 'Create RFQ', query: 'Create an RFQ for this purchase request' },
  { icon: Icons.sparkles, label: 'Approval workflow', query: 'Show me the approval workflow for this PR' },
  { icon: Icons.barChart, label: 'Check budget', query: 'Check if the budget for this PR is available in the department' },
  { icon: Icons.clipboardList, label: 'Amend PR', query: 'I need to amend this purchase request' },
]

// ── Vendor detail capabilities ────────────────────────────────────────────────
const VENDOR_CAPABILITIES: Capability[] = [
  { icon: Icons.search, label: 'Vendor performance', query: 'What is the performance history of this vendor?' },
  { icon: Icons.shield, label: 'Due diligence', query: 'Run a due diligence report on this vendor' },
  { icon: Icons.layers, label: 'Transaction history', query: 'Show me all purchase orders and transactions with this vendor' },
  { icon: Icons.clipboardList, label: 'Onboarding status', query: 'What is the current onboarding status of this vendor?' },
  { icon: Icons.sparkles, label: 'Compare vendors', query: 'Compare this vendor with other vendors in the same category' },
  { icon: Icons.barChart, label: 'Active contracts', query: 'Show me all active contracts with this vendor' },
]

// ── Contract hub capabilities ─────────────────────────────────────────────────
const CONTRACT_CAPABILITIES: Capability[] = [
  { icon: Icons.layers, label: 'List contracts', query: 'Show me all active contracts' },
  { icon: Icons.barChart, label: 'Contract stats', query: 'Show me contract KPIs — total value, expiring soon, by vendor' },
  { icon: Icons.search, label: 'Expiring soon', query: 'Which contracts are expiring in the next 90 days?' },
  { icon: Icons.fileText, label: 'Contract detail', query: 'Show me the details of this contract' },
  { icon: Icons.sparkles, label: 'Renewal risk', query: 'Which contracts have renewal risk or KPI breaches?' },
  { icon: Icons.clipboardList, label: 'Invoice match', query: 'Show me invoices pending 3-way match for this contract' },
]

// ── Invoice capabilities ──────────────────────────────────────────────────────
const INVOICE_CAPABILITIES: Capability[] = [
  { icon: Icons.layers, label: 'Pending invoices', query: 'Show me all invoices pending review or approval' },
  { icon: Icons.search, label: 'Check duplicate', query: 'Check if this invoice is a duplicate' },
  { icon: Icons.shield, label: '3-way match', query: 'Run a 3-way match check on pending invoices' },
  { icon: Icons.barChart, label: 'Invoice overview', query: 'Give me an overview of invoice status — pending, approved, overdue' },
  { icon: Icons.sparkles, label: 'Overdue invoices', query: 'Show me all overdue invoices that need attention' },
  { icon: Icons.clipboardList, label: 'Find invoice', query: 'Find a specific invoice by vendor or PO number' },
]

// ── Analytics/Dashboard capabilities ─────────────────────────────────────────
const ANALYTICS_CAPABILITIES: Capability[] = [
  { icon: Icons.barChart, label: 'Spend by department', query: 'Show me spend breakdown by department year-to-date' },
  { icon: Icons.layers, label: 'Top vendors by spend', query: 'Who are our top 10 vendors by spend this year?' },
  { icon: Icons.search, label: 'Cycle time analysis', query: 'What is the average PR-to-PO cycle time?' },
  { icon: Icons.sparkles, label: 'Generate report', query: 'Generate a procurement spend report for this quarter' },
  { icon: Icons.fileText, label: 'Category analysis', query: 'Show me spend by category and identify any anomalies' },
  { icon: Icons.shield, label: 'Compliance summary', query: 'Give me a compliance summary across all procurement activities' },
]

// ── AI policies capabilities ──────────────────────────────────────────────────
const AI_POLICIES_CAPABILITIES: Capability[] = [
  { icon: Icons.shield, label: 'Policy overview', query: 'List every active Auto Policy and what it enforces' },
  { icon: Icons.search, label: 'Price reasonableness', query: 'Which policy governs a fair and reasonable price determination?' },
  { icon: Icons.layers, label: 'Best Value', query: 'Explain the Best Value trade-off methodology this estate uses' },
  { icon: Icons.sparkles, label: 'FAR authority', query: 'What are the key obligations under FAR Part 12 for commercial items?' },
  { icon: Icons.fileText, label: 'Knowledge Hub search', query: 'Search the Knowledge Hub for small business set-aside requirements' },
  { icon: Icons.clipboardList, label: 'Policy compliance', query: 'How does this system enforce acquisition policy compliance?' },
]

function getCapabilitiesForPage(pathname: string): Capability[] {
  if (!pathname) return DEFAULT_CAPABILITIES

  // Solicitation workbench (a specific solicitation) — most specific, check first
  if (/^\/solicitations\/[A-Z0-9\-]+/.test(pathname)) return TENDER_WORKBENCH_CAPABILITIES

  // Solicitation Explorer
  if (pathname === '/solicitations' || pathname.startsWith('/solicitations?')) return TENDER_PIPELINE_CAPABILITIES

  // PR detail
  if (/^\/procurement\/requests\//.test(pathname)) return PR_DETAIL_CAPABILITIES

  // Procurement AP general
  if (pathname.startsWith('/procurement')) return PROCUREMENT_CAPABILITIES

  // Vendor detail
  if (/^\/vendors\//.test(pathname)) return VENDOR_CAPABILITIES

  // Contract detail or hub
  if (pathname.startsWith('/contracts')) return CONTRACT_CAPABILITIES

  // Invoices
  if (pathname.startsWith('/invoices')) return INVOICE_CAPABILITIES

  // Analytics/reports/dashboard
  if (pathname === '/' || pathname.startsWith('/reports')) return ANALYTICS_CAPABILITIES

  // AI Policies & Knowledge Hub
  if (pathname.startsWith('/ai/') || pathname.startsWith('/knowledge')) return AI_POLICIES_CAPABILITIES

  return DEFAULT_CAPABILITIES
}

interface CapabilityBubblesProps {
  onSelect: (query: string) => void
  currentPage?: string
}

export function CapabilityBubbles({ onSelect, currentPage = '/' }: CapabilityBubblesProps) {
  const capabilities = getCapabilitiesForPage(currentPage)

  return (
    <div className="flex flex-wrap justify-center gap-2 p-2">
      {capabilities.map((cap, i) => {
        const Icon = cap.icon
        return (
          <motion.button
            key={i}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.06, duration: 0.25, ease: 'easeOut' }}
            onClick={() => onSelect(cap.query)}
            className={cn(
              'flex items-center gap-2 px-3 py-2 rounded-full',
              'bg-white border border-brand-cornflower/20',
              'text-sm text-brand-navy',
              'shadow-sm',
              'transition-all duration-200',
              'hover:bg-brand-cornflower/10 hover:border-brand-cornflower/40 hover:shadow-md',
              'focus:outline-none focus:ring-2 focus:ring-brand-cornflower/50'
            )}
          >
            <Icon className="h-4 w-4 text-brand-cornflower" strokeWidth={1.5} />
            <span className="text-xs sm:text-sm font-medium">{cap.label}</span>
          </motion.button>
        )
      })}
    </div>
  )
}
